'use client';

import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { usePreviewSimulation } from '@/components/launchpad/use-preview-simulation';
import { MarketRowCardView } from '@/components/markets/market-row-card';
import { buildShowcaseOutcomes, CENTER_SHOWCASE_MARKET, PREVIEW_CONSENSUS_THRESHOLD_D9, SHOWCASE_MAX_SIM_TICKS, type ShowcaseMarketConfig, showcaseCadenceScaleForTick } from '@/components/support-mainnet/markets-showcase/fixture';
function usePageVisible() {
  const $ = _c(2);
  const [visible, setVisible] = useState(_temp);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const onVisibilityChange = () => {
        setVisible(document.visibilityState === "visible");
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return visible;
}
function _temp() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}
export function AnimatedShowcaseCard(t0) {
  const $ = _c(29);
  const {
    market: t1,
    title,
    simulationEnabled: t2
  } = t0;
  const market = t1 === undefined ? CENTER_SHOWCASE_MARKET : t1;
  const simulationEnabled = t2 === undefined ? true : t2;
  const [replayKey, setReplayKey] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const awaitingHoverReplayRef = useRef(false);
  const isHoveredRef = useRef(false);
  const pageVisible = usePageVisible();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      awaitingHoverReplayRef.current = false;
      setReplayKey(_temp2);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const requestReplay = t3;
  let t4;
  let t5;
  if ($[1] !== isHovered) {
    t4 = () => {
      isHoveredRef.current = isHovered;
    };
    t5 = [isHovered];
    $[1] = isHovered;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (isHoveredRef.current) {
        awaitingHoverReplayRef.current = true;
      }
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleBurstComplete = t6;
  const t7 = pageVisible && (simulationEnabled || isHovered);
  let t8;
  if ($[5] !== replayKey || $[6] !== t7) {
    t8 = {
      cadenceScaleForTick: showcaseCadenceScaleForTick,
      enabled: t7,
      maxTicks: SHOWCASE_MAX_SIM_TICKS,
      onBurstComplete: handleBurstComplete,
      replayKey
    };
    $[5] = replayKey;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const sim = usePreviewSimulation(market.outcomeLabels.length, market.startingShares, t8);
  let t9;
  if ($[8] !== market || $[9] !== sim) {
    t9 = buildShowcaseOutcomes(market, sim);
    $[8] = market;
    $[9] = sim;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const outcomes = t9;
  let t10;
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsHovered(true);
      requestReplay();
    };
    t11 = () => {
      setIsHovered(false);
      awaitingHoverReplayRef.current = false;
    };
    t12 = () => {
      if (!awaitingHoverReplayRef.current) {
        return;
      }
      requestReplay();
    };
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
  } else {
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
  }
  const t13 = market.pubkeySuffix;
  const t14 = market.cover;
  const t15 = market.location;
  const t16 = market.category;
  const t17 = sim.volumeTotalD6;
  const t18 = sim.prizePoolD6;
  const t19 = sim.uniqueTradersTotal;
  let t20;
  let t21;
  if ($[14] !== sim.liveTrades) {
    t20 = [...sim.liveTrades];
    t21 = sim.liveTrades.map(_temp3);
    $[14] = sim.liveTrades;
    $[15] = t20;
    $[16] = t21;
  } else {
    t20 = $[15];
    t21 = $[16];
  }
  let t22;
  if ($[17] !== market.category || $[18] !== market.cover || $[19] !== market.location || $[20] !== market.pubkeySuffix || $[21] !== outcomes || $[22] !== sim.prizePoolD6 || $[23] !== sim.uniqueTradersTotal || $[24] !== sim.volumeTotalD6 || $[25] !== t20 || $[26] !== t21 || $[27] !== title) {
    t22 = <div className="w-full max-w-[408px]" data-testid="markets-showcase-animated-card" onMouseEnter={t10} onMouseLeave={t11} onPointerMove={t12}><MarketRowCardView outcomeLayout="stacked" linkDisabled={true} pubkey={t13} title={title} coverImageUrl={t14} coverImageNsfw={false} location={t15} category={t16} volumeTotal={t17} prizePool={t18} uniqueTradersTotal={t19} outcomes={outcomes} position={0} activeSort="volume24h" consensusThreshold={PREVIEW_CONSENSUS_THRESHOLD_D9} liveTrades={t20} liveSinceMountIds={t21} suppressOutcomeReveal={true} /></div>;
    $[17] = market.category;
    $[18] = market.cover;
    $[19] = market.location;
    $[20] = market.pubkeySuffix;
    $[21] = outcomes;
    $[22] = sim.prizePoolD6;
    $[23] = sim.uniqueTradersTotal;
    $[24] = sim.volumeTotalD6;
    $[25] = t20;
    $[26] = t21;
    $[27] = title;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  return t22;
}
function _temp3(trade) {
  return trade.id;
}
function _temp2(key) {
  return key + 1;
}